// src/utils/sintesiEconomicaRelazione.js
// ═══════════════════════════════════════════════════════════════════════════
// SINTESI ECONOMICA A COLONNE DELLA RELAZIONE DELLA COMMESSA (W441)
// ═══════════════════════════════════════════════════════════════════════════
// La parte che il titolare legge per prima: ricavi, costi per voce (previsto e sostenuto),
// risultato e fatturazione, in tabelle. Solo numeri già registrati, con le STESSE regole del
// Controllo Economico (utils/economiaCommessa.js): materiali = ordini di acquisto + materiali
// a mano; manodopera e noleggi calcolati dal Controllo Economico (costiCalcolati sul budget);
// tutto il resto dai movimenti, per voce. Logica pura, provata a parte.
// ═══════════════════════════════════════════════════════════════════════════
import { numIt, eRicavo, contaComeCosto, imponibileOda, STATI_ODA_CONTEGGIATI, importoCommessa, ricaviDaMovimenti, marginePrevistoCommessa } from "./economiaCommessa";

// Voci nell'ordine del Controllo Economico; quelle personalizzate dell'azienda vanno in fondo
export const VOCI_COSTO = [
  { id: "materiali", label: "Materiali" },
  { id: "personale", label: "Manodopera" },
  { id: "subappaltatore", label: "Subappaltatori" },
  { id: "noleggi_mezzi_opera", label: "Noleggi mezzi d'opera" },
  { id: "professionisti", label: "Professionisti esterni" },
  { id: "pratiche", label: "Pratiche" },
  { id: "trasporti", label: "Trasporti" },
  { id: "vitto", label: "Vitto e alloggio" },
  { id: "nota_spese", label: "Note spese" },
  { id: "varie", label: "Varie" },
];
const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const e2 = (n) => n === null || n === undefined ? "—" : "€ " + Number(n).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const p1 = (x) => x === null || x === undefined ? "—" : Number(x).toLocaleString("it-IT", { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + "%";
export const euroRelazione = e2;
export const pctRelazione = p1;

/**
 * budget: documento budgetCommessa (con variantiCalcolate già aggiornate) · movimenti e ordini
 * della commessa · rVar: riepilogoVarianti · margineOfferta: % prevista in offerta (o null)
 * margineMedio: media delle commesse chiuse dell'azienda (o null) · fatturatoSAL: dal Computo SAL (o null)
 */
export function sintesiEconomicaDettagliata({ budget = {}, movimenti = [], ordini = [], projectId, rVar = {}, margineOfferta = null, margineMedio = null, fatturatoSAL = null, fontOre }) {
  // W452: stessa fonte delle ore del Controllo Economico. Prima i movimenti dei cartellini
  // contavano sempre: con fonte Programmazione o Rapportini la manodopera entrava due volte
  // (cartellini + ore calcolate) e la tabella dava un totale diverso dal testo della Relazione.
  const fonte = fontOre || budget?.costiCalcolati?.fonteOre;
  // ── Ricavi
  const contratto = importoCommessa(budget);
  const varianti = numIt(rVar.totale);
  const altriRicavi = ricaviDaMovimenti(movimenti);
  const ricavi = r2(contratto + varianti + altriRicavi);
  const righeRicavi = [
    { voce: "Importo contrattuale", importo: r2(contratto) },
    ...(varianti > 0 ? [{ voce: "Varianti autorizzate dal Cliente", importo: r2(varianti) }] : []),
    ...(altriRicavi > 0 ? [{ voce: "Altri ricavi registrati", importo: r2(altriRicavi) }] : []),
  ];

  // ── Costi per voce
  const somma = {}; const etichette = {};
  const aggiungi = (id, v, label) => { if (!v) return; somma[id] = (somma[id] || 0) + v; if (label && !etichette[id]) etichette[id] = label; };
  const ordiniCommessa = ordini.filter(o => o && !o.eliminato && (o.commessaId === projectId || o.projectId === projectId) && STATI_ODA_CONTEGGIATI.includes(o.stato));
  const daOrdini = ordiniCommessa.reduce((t, o) => t + imponibileOda(o), 0);
  let materialiAMano = 0;
  movimenti.forEach(m => {
    if (!contaComeCosto(m, fonte) || eRicavo(m)) return;
    // le ore dal cartellino (Presenze) arrivano come «manodopera»: stessa voce della manodopera a mano
    const id = m.categoriaId === "manodopera" ? "personale" : (m.categoriaId || "varie");
    const v = numIt(m.importo);
    if (id === "materiali") materialiAMano += v;
    aggiungi(id, v, m.categoriaLabel);
  });
  aggiungi("materiali", daOrdini);
  const cc = budget?.costiCalcolati || {};
  const manodoperaCalcolata = (cc.fonteOre && cc.fonteOre !== "presenze" ? numIt(cc.manodopera) : 0) + numIt(cc.diario);
  aggiungi("personale", manodoperaCalcolata);
  aggiungi("noleggi_mezzi_opera", numIt(cc.noleggi));
  const costi = r2(Object.values(somma).reduce((t, v) => t + v, 0));
  const previstoVoce = { materiali: numIt(budget?.costoMaterialiPrev), personale: numIt(budget?.costoPersonalePrev) };
  const note = {
    materiali: daOrdini > 0 && materialiAMano > 0 ? `ordini ${e2(daOrdini)} + a mano ${e2(materialiAMano)}` : daOrdini > 0 ? "da ordini di acquisto" : "",
    noleggi_mezzi_opera: numIt(cc.noleggi) > 0 ? "tariffa e trasporti del noleggiatore" : "",
  };
  const noti = VOCI_COSTO.map(v => v.id);
  const ids = [...noti.filter(id => somma[id]), ...Object.keys(somma).filter(id => !noti.includes(id)).sort()];
  const righeCosti = ids.map(id => ({
    id, voce: VOCI_COSTO.find(v => v.id === id)?.label || etichette[id] || id,
    previsto: previstoVoce[id] > 0 ? r2(previstoVoce[id]) : null,
    sostenuto: r2(somma[id]), quota: costi > 0 ? (somma[id] / costi) * 100 : 0, nota: note[id] || "",
  }));
  // voci previste ma senza costi ancora sostenuti: si vedono (con 0), così il confronto è completo
  ["materiali", "personale"].forEach(id => {
    if (previstoVoce[id] > 0 && !somma[id]) righeCosti.unshift({ id, voce: VOCI_COSTO.find(v => v.id === id).label, previsto: r2(previstoVoce[id]), sostenuto: 0, quota: 0, nota: "" });
  });
  const mp = marginePrevistoCommessa({ budget, ricavi, costiReali: costi, costoPrevistoVarianti: rVar.costoPrevisto });
  const costoPrevisto = mp.costoPrevisto > 0 ? r2(mp.costoPrevisto) : null;
  const scostamento = costoPrevisto !== null ? r2(costoPrevisto - costi) : null; // >0 = sotto il previsto

  // ── Risultato
  const margine = r2(ricavi - costi);
  const marginePct = ricavi > 0 ? (margine / ricavi) * 100 : null;
  const risultato = {
    margine, marginePct,
    margineOfferta: margineOfferta ?? null,
    differenzaPunti: marginePct !== null && margineOfferta !== null && margineOfferta !== undefined ? marginePct - margineOfferta : null,
    margineMedio: margineMedio ?? null,
    marginePrevisto: mp.marginePrevisto !== null ? r2(mp.marginePrevisto) : null,
  };

  // ── Fatturazione: totali delle richieste salvati sul budget (fatturazioneCalcolata), oppure il SAL
  const fc = budget?.fatturazioneCalcolata || null;
  const fatturato = r2(fc && fc.nRichieste ? numIt(fc.importoFatturato) : numIt(fatturatoSAL));
  const varFatturate = r2(numIt(rVar.fatturato));
  const inAttesa = r2(fc ? numIt(fc.importoInAttesa) : 0);
  const varDaFatturare = r2(numIt(rVar.daFatturare));
  const residuo = r2(Math.max(0, ricavi - fatturato - varFatturate));
  const fatturazione = { totale: ricavi, fatturato, varFatturate, inAttesa, nInAttesa: fc ? (fc.nInAttesa || 0) : 0, varDaFatturare, nVarDaFatturare: rVar.nDaFatturare || 0, residuo };

  return {
    ricavi: { righe: righeRicavi, totale: ricavi, variantiInAttesa: r2(numIt(rVar.inAttesa)) },
    costi: { righe: righeCosti, totale: costi, previsto: costoPrevisto, scostamento },
    risultato, fatturazione,
  };
}

// ── Testo a colonne (tabulazioni) per «Copia testo»: incollato in Excel ogni colonna va nella sua cella

export function righeTestoSintesi(s) {
  if (!s) return [];
  const t = (...c) => c.join("\t");
  const out = ["Ricavi", t("Voce", "Importo"), ...s.ricavi.righe.map(r => t(r.voce, e2(r.importo))), t("Totale commessa", e2(s.ricavi.totale))];
  if (s.ricavi.variantiInAttesa > 0) out.push(`(fuori dal totale: varianti valorizzate in attesa di autorizzazione ${e2(s.ricavi.variantiInAttesa)})`);
  out.push("", "Costi per voce", t("Voce", "Previsto", "Sostenuto", "% sui costi"),
    ...s.costi.righe.map(r => t(r.voce + (r.nota ? ` (${r.nota})` : ""), e2(r.previsto), e2(r.sostenuto), p1(r.quota))),
    t("Totale costi", e2(s.costi.previsto), e2(s.costi.totale), "100,0%"));
  if (s.costi.scostamento !== null) out.push(s.costi.scostamento >= 0 ? `Sotto il previsto di ${e2(s.costi.scostamento)}` : `Oltre il previsto di ${e2(-s.costi.scostamento)}`);
  const ri = s.risultato;
  out.push("", "Risultato", t("Margine attuale", "Margine previsto in offerta", "Differenza", "Media commesse chiuse"),
    t(`${e2(ri.margine)} (${p1(ri.marginePct)})`, p1(ri.margineOfferta), ri.differenzaPunti === null ? "—" : `${ri.differenzaPunti >= 0 ? "+" : ""}${Number(ri.differenzaPunti).toLocaleString("it-IT", { maximumFractionDigits: 1, minimumFractionDigits: 1 })} punti`, p1(ri.margineMedio)));
  const f = s.fatturazione;
  out.push("", "Fatturazione", t("Voce", "Importo"),
    t("Totale commessa", e2(f.totale)), t("Già fatturato", e2(f.fatturato)),
    ...(f.varFatturate > 0 ? [t("Varianti già fatturate", e2(f.varFatturate))] : []),
    ...(f.inAttesa > 0 ? [t(`Richieste di fatturazione in attesa (${f.nInAttesa})`, e2(f.inAttesa))] : []),
    ...(f.varDaFatturare > 0 ? [t(`Varianti da fatturare (${f.nVarDaFatturare})`, e2(f.varDaFatturare))] : []),
    t("Ancora da fatturare", e2(f.residuo)));
  return out;
}
